// src/components/schedule/QuickActions.jsx
import React, { useState, useEffect } from 'react';
import {
  triggerSync,
  triggerFastContentSync,
  resetStuckSyncs,
  triggerYesterdayToTodayAttemptsSync
} from '../../api/syncApi';
import { fetchCourses } from '../../api/adminApi';
import { toast } from 'react-toastify';
import useAuth from '../../hooks/useAuth';
import { useSocketContext } from '../../context/SocketContext';
import SyncService from '../../services/SyncService';
import { getStoredUsername } from '../../utils/storedUsername';

/* ── Sync type definitions ────────────────────────────────── */
const SYNC_TYPES = [
  { key: 'courses',           runType: 'course-sync',            icon: 'bi-book',              label: 'Courses' },
  { key: 'chapters',          runType: 'chapter-sync',           icon: 'bi-list-nested',       label: 'Chapters' },
  { key: 'content',           runType: 'content-sync',           icon: 'bi-file-earmark-text', label: 'Content' },
  { key: 'live_class_content',     runType: 'live-class-content-sync',     icon: 'bi-camera-video', label: 'Live Class Content' },
  { key: 'jee_live_class_content', runType: 'jee-live-class-content-sync', icon: 'bi-camera-video', label: 'JEE Live Class Content' },
  { key: 'mentors',           runType: 'mentor-sync',            icon: 'bi-person',            label: 'Mentors' },
  { key: 'exams',             runType: 'exams-sync',             icon: 'bi-card-list',         label: 'Exams' },
  { key: 'attempts',          runType: 'attempts-sync',          icon: 'bi-clipboard-check',   label: 'Attempts' },
  { key: 'batches',           runType: 'batch-sync',             icon: 'bi-people',            label: 'Batches' },
  { key: 'users',             runType: 'users-sync',             icon: 'bi-people-fill',       label: 'Users' },
  { key: 'attempt_answers',   runType: 'attempt-answers-sync',   icon: 'bi-record-circle',     label: 'Attempt Answers' },
  { key: 'content_attempts',  runType: 'content-attempts-sync',  icon: 'bi-pencil-square',     label: 'Content Attempts' },
  { key: 'user_conference',   runType: 'user-conference-sync',   icon: 'bi-person-video3',     label: 'User Conference' },
  { key: 'video_conference',  runType: 'video-conference-sync',  icon: 'bi-camera-video',      label: 'Video Conference' },
  { key: 'single_attempts',   runType: 'single-attempts-sync',   icon: 'bi-arrow-repeat',      label: 'Each Attempt' },
];

/* ── PasswordModal ───────────────────────────────────────── */
const PasswordModal = ({ title, description, onConfirm, onCancel, isLoading }) => {
  const [password, setPassword] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onConfirm(password);
  };

  return (
    <div className="schedule-modal-overlay" onClick={onCancel}>
      <div className="schedule-modal" onClick={(e) => e.stopPropagation()}>
        <div className="schedule-modal-header">
          <h2 className="schedule-modal-title">
            <i className="bi bi-lock" style={{ color: 'var(--accent-orange)' }} aria-hidden="true" />
            Authentication Required
          </h2>
          <button
            className="schedule-modal-close"
            onClick={onCancel}
            disabled={isLoading}
            aria-label="Close"
          >
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="schedule-modal-body">
            <p className="schedule-auth-note">{description}</p>

            <div className="schedule-form-group">
              <label className="schedule-form-label" htmlFor="qa-password">
                Password
              </label>
              <input
                id="qa-password"
                type="password"
                className="schedule-form-input"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoFocus
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="schedule-modal-footer">
            <button
              type="button"
              className="qa-btn qa-btn--ghost"
              onClick={onCancel}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="qa-btn qa-btn--blue"
              disabled={isLoading || !password}
            >
              {isLoading ? (
                <>
                  <span className="schedule-spinner" aria-hidden="true" />
                  Validating…
                </>
              ) : (
                'Continue'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ── FastContentSyncModal ────────────────────────────────── */
const FastContentSyncModal = ({ onClose, onSyncTriggered, isLoading, setIsLoading }) => {
  const { login } = useAuth();
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [courseSearch, setCourseSearch] = useState('');
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [password, setPassword] = useState('');

  useEffect(() => {
    let cancelled = false;
    setCoursesLoading(true);
    fetchCourses(1, 100, '')
      .then((data) => {
        if (!cancelled) {
          setCourses(data.courses || data.results || []);
        }
      })
      .catch(() => toast.error('Failed to load courses'))
      .finally(() => {
        if (!cancelled) setCoursesLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const filtered = courses.filter(
    (c) =>
      c.title?.toLowerCase().includes(courseSearch.toLowerCase()) ||
      String(c.id).includes(courseSearch)
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCourseId) {
      toast.error('Please select a course');
      return;
    }
    setIsLoading(true);
    try {
      const username = getStoredUsername();
      const ok = await login({ username, password });
      if (ok) {
        const result = await triggerFastContentSync(selectedCourseId);
        toast.success(`Fast content sync started for course ${selectedCourseId}`);
        if (onSyncTriggered) onSyncTriggered(result?.syncId);
        onClose();
      } else {
        toast.error('Invalid credentials. Sync operation canceled.');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to start fast content sync');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="schedule-modal-overlay" onClick={onClose}>
      <div className="schedule-modal schedule-modal--lg" onClick={(e) => e.stopPropagation()}>
        <div className="schedule-modal-header">
          <h2 className="schedule-modal-title">
            <i className="bi bi-lightning-charge-fill" style={{ color: 'var(--accent-orange)' }} aria-hidden="true" />
            Fast Content Sync
          </h2>
          <button
            className="schedule-modal-close"
            onClick={onClose}
            disabled={isLoading}
            aria-label="Close"
          >
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="schedule-modal-body">
            <p className="schedule-auth-note">
              Syncs content for a single course. Skips unchanged content and fetches chapters in
              parallel — much faster than a full sync.
            </p>

            {/* Course search */}
            <div className="schedule-form-group">
              <label className="schedule-form-label">Select Course</label>
              <input
                type="text"
                className="schedule-form-input"
                placeholder="Search by name or ID…"
                value={courseSearch}
                onChange={(e) => setCourseSearch(e.target.value)}
              />
            </div>

            {/* Course list */}
            <div className="fast-sync-course-list">
              {coursesLoading ? (
                <div className="fast-sync-empty">
                  <span className="schedule-spinner" style={{ color: 'var(--text-muted)' }} aria-hidden="true" />
                  {' '}Loading courses…
                </div>
              ) : filtered.length === 0 ? (
                <div className="fast-sync-empty">No courses found</div>
              ) : (
                filtered.map((course) => (
                  <div
                    key={course.id}
                    className={`fast-sync-course-item${selectedCourseId === course.id ? ' fast-sync-course-item--selected' : ''}`}
                    onClick={() => setSelectedCourseId(course.id)}
                  >
                    <input
                      type="radio"
                      checked={selectedCourseId === course.id}
                      onChange={() => setSelectedCourseId(course.id)}
                      onClick={(e) => e.stopPropagation()}
                      style={{ accentColor: 'var(--accent-blue)', flexShrink: 0 }}
                    />
                    <div>
                      <div className="fast-sync-course-title">{course.title}</div>
                      <div className="fast-sync-course-id">ID: {course.id}</div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Password */}
            <div className="schedule-form-group">
              <label className="schedule-form-label" htmlFor="fast-sync-password">
                Password
              </label>
              <input
                id="fast-sync-password"
                type="password"
                className="schedule-form-input"
                placeholder="Enter your password to confirm"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="schedule-modal-footer">
            <button
              type="button"
              className="qa-btn qa-btn--ghost"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="qa-btn qa-btn--orange"
              disabled={isLoading || !selectedCourseId || !password}
            >
              {isLoading ? (
                <>
                  <span className="schedule-spinner" aria-hidden="true" />
                  Starting…
                </>
              ) : (
                <>
                  <i className="bi bi-lightning-charge-fill" aria-hidden="true" />
                  Start Fast Sync
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ── QuickActions ────────────────────────────────────────── */
const QuickActions = ({ onSyncTriggered }) => {
  const { login } = useAuth();
  const { runningSyncs } = useSocketContext();

  const [isLoading, setIsLoading] = useState(false);

  // Password modal state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // { type, label }

  // Fast content sync modal
  const [showFastSyncModal, setShowFastSyncModal] = useState(false);

  /* ── Initiate any sync via password modal ──────────────── */
  const initiateSync = (type, label) => {
    setPendingAction({ type, label });
    setShowPasswordModal(true);
  };

  const cancelPasswordModal = () => {
    setShowPasswordModal(false);
    setPendingAction(null);
  };

  const handlePasswordConfirm = async (password) => {
    setIsLoading(true);
    try {
      const username = getStoredUsername();
      const ok = await login({ username, password });
      if (!ok) {
        toast.error('Invalid credentials. Sync operation canceled.');
        return;
      }

      if (pendingAction.type === 'all') {
        const result = await triggerSync('all');
        toast.success('Full sync triggered successfully');
        if (onSyncTriggered) onSyncTriggered(result?.syncId);
      } else if (pendingAction.type === 'attempts_yesterday_today') {
        const result = await triggerYesterdayToTodayAttemptsSync();
        toast.success('Yesterday to today attempts sync triggered successfully');
        if (onSyncTriggered) {
          onSyncTriggered(result?.syncId);
        }
      } else if (pendingAction.type === 'reset_stuck') {
        const result = await resetStuckSyncs();
        toast.success(result.message || 'Stuck syncs have been reset');
        if (onSyncTriggered) onSyncTriggered();
      } else {
        const result = await triggerSync(pendingAction.type);
        toast.success(`${pendingAction.label} sync triggered successfully`);
        if (onSyncTriggered) onSyncTriggered(result?.syncId);
      }

      setShowPasswordModal(false);
      setPendingAction(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Operation failed');
    } finally {
      setIsLoading(false);
    }
  };

  const isAnySyncRunning = runningSyncs && runningSyncs.length > 0;

  return (
    <>
      <div className="quick-actions-grid">
        {/* ── Card 1: Sync All ──────────────────────────── */}
        <div className="theme-card qa-card qa-card--green">
          <div className="qa-card-header">
            <i className="bi bi-arrow-repeat qa-card-icon" aria-hidden="true" />
            <div>
              <h3 className="qa-card-title">Sync All Data</h3>
            </div>
          </div>
          <p className="qa-card-desc">
            Triggers a full sync of all data types: courses, chapters, content, mentors, exams,
            attempts, batches, and users.
          </p>
          <div>
            <button
              className="qa-btn qa-btn--green"
              onClick={() => initiateSync('all', 'All Data')}
              disabled={isLoading}
            >
              {isLoading && pendingAction?.type === 'all' ? (
                <>
                  <span className="schedule-spinner" aria-hidden="true" />
                  Triggering…
                </>
              ) : (
                <>
                  <i className="bi bi-play-fill" aria-hidden="true" />
                  Sync All
                </>
              )}
            </button>
          </div>
        </div>

        {/* ── Card 2: Sync by Type ──────────────────────── */}
        <div className="theme-card qa-card qa-card--blue">
          <div className="qa-card-header">
            <i className="bi bi-grid-3x3-gap qa-card-icon" aria-hidden="true" />
            <div>
              <h3 className="qa-card-title">Sync by Type</h3>
            </div>
          </div>
          <p className="qa-card-desc">Trigger a sync for a specific data type.</p>
          <div className="sync-type-grid">
            {SYNC_TYPES.map(({ key, runType, icon, label }) => {
              const running = SyncService.isSyncRunning(runningSyncs, runType);
              return (
                <button
                  key={key}
                  className={`sync-type-btn${running ? ' sync-type-btn--running' : ''}`}
                  onClick={() => initiateSync(key, label)}
                  disabled={isLoading}
                  title={running ? `${label} is currently running` : `Sync ${label}`}
                >
                  {running ? (
                    <span className="running-badge-dot" aria-hidden="true" />
                  ) : (
                    <i className={`bi ${icon}`} aria-hidden="true" />
                  )}
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Card 3: Fast Content Sync ─────────────────── */}
        <div className="theme-card qa-card qa-card--orange">
          <div className="qa-card-header">
            <i className="bi bi-lightning-charge-fill qa-card-icon" aria-hidden="true" />
            <div>
              <h3 className="qa-card-title">Fast Content Sync</h3>
            </div>
          </div>
          <p className="qa-card-desc">
            Sync content for a single course. Skips unchanged items and fetches chapters in
            parallel — significantly faster than a full sync.
          </p>
          <div>
            <button
              className="qa-btn qa-btn--orange"
              onClick={() => setShowFastSyncModal(true)}
              disabled={isLoading}
            >
              <i className="bi bi-lightning-charge-fill" aria-hidden="true" />
              Select Course &amp; Sync
            </button>
          </div>
        </div>

        {/* ── Card 4: Reset Stuck Syncs ─────────────────── */}
        <div className="theme-card qa-card qa-card--blue">
          <div className="qa-card-header">
            <i className="bi bi-calendar-range qa-card-icon" aria-hidden="true" />
            <div>
              <h3 className="qa-card-title">Yesterday to Today Attempts</h3>
              {(SyncService.isSyncRunning(runningSyncs, 'content-attempts-sync') ||
                SyncService.isSyncRunning(runningSyncs, 'exam-attempts-sync')) && (
                <span className="running-badge" style={{ marginTop: 4 }}>
                  <span className="running-badge-dot" aria-hidden="true" />
                  Sync running
                </span>
              )}
            </div>
          </div>
          <p className="qa-card-desc">
            Sync only content attempts and exam attempts for the yesterday-to-today
            window.
          </p>
          <div>
            <button
              className="qa-btn qa-btn--blue"
              onClick={() => initiateSync('attempts_yesterday_today', 'Yesterday to Today Attempts')}
              disabled={isLoading}
            >
              {isLoading && pendingAction?.type === 'attempts_yesterday_today' ? (
                <>
                  <span className="schedule-spinner" aria-hidden="true" />
                  Triggering…
                </>
              ) : (
                <>
                  <i className="bi bi-play-fill" aria-hidden="true" />
                  Sync Yesterday to Today
                </>
              )}
            </button>
          </div>
        </div>

        {/* ── Card 5: Reset Stuck Syncs ─────────────────── */}
        <div className="theme-card qa-card qa-card--red">
          <div className="qa-card-header">
            <i className="bi bi-x-octagon qa-card-icon" aria-hidden="true" />
            <div>
              <h3 className="qa-card-title">Reset Stuck Syncs</h3>
              {isAnySyncRunning && (
                <span className="running-badge" style={{ marginTop: 4 }}>
                  <span className="running-badge-dot" aria-hidden="true" />
                  Syncs running
                </span>
              )}
            </div>
          </div>
          <p className="qa-card-desc">
            If a sync appears stuck, use this to forcefully reset all running sync operations so
            new ones can be triggered.
          </p>
          <div>
            <button
              className="qa-btn qa-btn--red"
              onClick={() => initiateSync('reset_stuck', 'Reset Stuck Syncs')}
              disabled={isLoading}
            >
              <i className="bi bi-arrow-counterclockwise" aria-hidden="true" />
              Reset Stuck Syncs
            </button>
          </div>
        </div>
      </div>

      {/* ── Password Confirmation Modal ───────────────────── */}
      {showPasswordModal && pendingAction && (
        <PasswordModal
          title="Authentication Required"
          description={
            <>
              Enter your password to continue with the{' '}
              <strong>{pendingAction.label}</strong> operation.
            </>
          }
          onConfirm={handlePasswordConfirm}
          onCancel={cancelPasswordModal}
          isLoading={isLoading}
        />
      )}

      {/* ── Fast Content Sync Modal ───────────────────────── */}
      {showFastSyncModal && (
        <FastContentSyncModal
          onClose={() => setShowFastSyncModal(false)}
          onSyncTriggered={onSyncTriggered}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
        />
      )}
    </>
  );
};

export default QuickActions;
